import React, { useState, forwardRef } from "react";
import { StyleSheet, Animated, View, Pressable, Dimensions, Text } from "react-native";
import { GearIcon, SignOutIcon, XIcon } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import TabNavigator from "../../common/general/TabNavigator/TabNavigator";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import UserInitial from "../../general/UserInitial/UserInitial";
import ProgressTab from "./ProgressTab";
import ResourcesTab from "./ResourcesTab";
import SupportTab from "./SupportTab";

interface SlideInMenuProps {
  visible: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  slideAnim: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meData: any;
  onSettingsPress: () => void;
  hasDegrees: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  progressStats: any;
  degreeName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coursesData: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resourcesData: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supportData: any;
  onLogout: () => void;
  onClose: () => void;
}

const SlideInMenu = forwardRef<View, SlideInMenuProps>(function SlideInMenu(
  {
    visible,
    slideAnim,
    meData,
    onSettingsPress,
    hasDegrees,
    progressStats,
    degreeName,
    coursesData,
    resourcesData,
    supportData,
    onLogout,
    onClose,
  },
  ref
) {
  const [activeTab, setActiveTab] = useState("progress");

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;
  const integrationId = appConfig.axioSchools[0];

  const { isMedium } = useResponsiveScreen();
  const isMobile = !isMedium;

  const screenHeight = Dimensions.get("window").height;

  const userName = meData?.me?.name;
  let created = meData?.me?.created;
  if (created) {
    const date = new Date(created);
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    created = `${month}, ${year}`;
  }

  if (!visible) return null;

  const tabData = [{ key: "progress", title: "Progress" }];
  if (resourcesData.isShow) {
    tabData.push({ key: "resources", title: "Resources" });
  }
  if (supportData.isShow) {
    tabData.push({ key: "support", title: "Support" });
  }

  return (
    <Animated.View
      ref={ref}
      role="dialog"
      aria-label="Course progress"
      accessibilityRole="none"
      accessibilityLabel="Course progress"
      style={[
        styles.sideMenu,
        isMobile ? styles.sideMenuMobile : {},
        { height: screenHeight, transform: [{ translateX: slideAnim }] },
      ]}>
      <View style={styles.sideMenuContainer}>
        <View style={styles.userInfoRow}>
          <UserInitial initial={userName?.charAt(0)} type="menu" />
          <CustomSpacing type="horizontal" size="s" />
          <View style={styles.userTextContainer}>
            <Text style={styles.userName}>{userName}</Text>
            <CustomText style={styles.joinDate} text="student_since" interpolation={{ created }} />
          </View>
          <Pressable
            role="button"
            aria-label="Close menu"
            style={styles.closeButton}
            onPress={onClose}
            accessibilityLabel="Close menu">
            <XIcon size={16} color="#475467" />
          </Pressable>
        </View>

        <TabNavigator
          tabData={tabData}
          activeTab={activeTab}
          onTabPress={setActiveTab}
          activeTabColor={primaryColor}
          isFullWidth={true}
          containerStyle={{ paddingHorizontal: 16 }}
          rightButtonComponent={null}
        />

        <View style={styles.contentContainer}>
          {activeTab === "progress" && (
            <ProgressTab
              integrationId={integrationId}
              hasDegrees={hasDegrees}
              degreeName={degreeName}
              progressStats={progressStats}
              coursesData={coursesData}
            />
          )}
          {activeTab === "resources" && <ResourcesTab resourcesData={resourcesData} />}
          {activeTab === "support" && <SupportTab supportData={supportData} />}
        </View>

        <View style={styles.bottomButtonsContainer}>
          <CustomButton
            text="Settings"
            leftIcon={<GearIcon size={16} />}
            size="s"
            styleType="transparent"
            textStyle={styles.menuButton}
            aria-label="View Settings"
            onPress={onSettingsPress}
            accessibilityLabel="View Settings"
            accessibilityHint="Navigate to user settings"
          />
          <CustomButton
            text="Sign Out"
            leftIcon={<SignOutIcon size={16} color="#C01048" />}
            size="s"
            styleType="transparent"
            textStyle={styles.menuButton}
            aria-label="Sign Out"
            onPress={onLogout}
            accessibilityLabel="Sign Out"
            accessibilityHint="Sign out of the application"
          />
        </View>
      </View>
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  sideMenu: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 360,
    zIndex: 9999,
    backgroundColor: "#fff",
  },
  sideMenuMobile: {
    top: -85,
  },
  sideMenuContainer: {
    flex: 1,
    backgroundColor: "white",
    borderLeftWidth: 1,
    borderLeftColor: "#e0e0e0",
  },
  userInfoRow: {
    marginBottom: 12,
    paddingVertical: 19,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fcfcfd",
    borderBottomWidth: 1,
    borderBottomColor: "#eaecf0",
  },
  userTextContainer: {
    flex: 1,
  },
  userName: {
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 24,
    color: "#101828",
  },
  joinDate: {
    fontWeight: "400",
    fontSize: 12,
    lineHeight: 18,
    color: "#667085",
  },
  closeButton: {
    padding: 5,
    marginLeft: 8,
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    padding: 16,
    marginBottom: 24,
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    flex: 1,
  },
  bottomButtonsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#fcfcfd",
    borderTopWidth: 1,
    borderTopColor: "#eaecf0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuButton: {
    fontWeight: "400",
    color: "#475467",
  },
});

export default SlideInMenu;
